import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronRight, File, Folder, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  HydratedOrganizationFile,
  ORGANIZATION_FILE_ENTRY_TYPE,
  OrganizationFileBreadcrumb,
} from "@forgepoint/shared";
import { useFilesStore } from "./filesStore";
import { VaultBreadcrumbs } from "./VaultBreadcrumbs";

interface MoveFileDialogProps {
  /** Id of the entry to move; null keeps the dialog closed. */
  fileId: string | null;
  onClose: () => void;
  onMoved: () => void;
}

/**
 * Dialog for moving a vault entry into another folder. A small folder browser
 * navigates the vault; the folder in view is the move destination. Files in
 * the viewed folder are shown for context but are not selectable, and the
 * entry being moved is hidden so it cannot be dropped into its own subtree.
 */
export function MoveFileDialog({
  fileId,
  onClose,
  onMoved,
}: MoveFileDialogProps) {
  const fetchFolderListing = useFilesStore((state) => state.fetchFolderListing);
  const updateFile = useFilesStore((state) => state.updateFile);
  const fileLoadingStates = useFilesStore((state) => state.fileLoadingStates);
  const files = useFilesStore((state) => state.files);
  const [destinationFolderId, setDestinationFolderId] = useState<string | null>(
    null,
  );
  const [entryIds, setEntryIds] = useState<string[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<OrganizationFileBreadcrumb[]>(
    [],
  );
  const [listingTotal, setListingTotal] = useState(0);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);

  const file = fileId ? files.get(fileId) : undefined;
  const open = file !== undefined;
  const isFolder = file?.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER;
  const isMoving = file
    ? (fileLoadingStates.get(file.id)?.isLoadingUpdate ?? false)
    : false;
  const isCurrentParent =
    destinationFolderId === (file?.parentFolderId ?? null);
  const visibleEntries = entryIds
    .map((id) => files.get(id))
    .filter(
      (entry): entry is HydratedOrganizationFile =>
        entry !== undefined && entry.id !== fileId,
    );
  const visibleFolders = visibleEntries.filter(
    (entry) => entry.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER,
  );
  const visibleFiles = visibleEntries.filter(
    (entry) => entry.type === ORGANIZATION_FILE_ENTRY_TYPE.FILE,
  );
  const hiddenEntryCount = Math.max(0, listingTotal - entryIds.length);

  useEffect(() => {
    if (!open) {
      setDestinationFolderId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let active = true;
    const load = async () => {
      setIsLoadingEntries(true);
      const listing = await fetchFolderListing(destinationFolderId);
      if (!active) {
        return;
      }
      if (listing) {
        setEntryIds(listing.entryIds);
        setBreadcrumbs(listing.breadcrumbs);
        setListingTotal(listing.total);
      } else {
        toast.error("Failed to load folders");
      }
      setIsLoadingEntries(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [open, destinationFolderId, fetchFolderListing]);

  const handleMove = async () => {
    if (!file) {
      return;
    }

    const movedFile = await updateFile({
      organizationFileId: file.id,
      parentFolderId: destinationFolderId,
    });
    if (movedFile) {
      toast.success(isFolder ? "Folder moved" : "File moved");
      onClose();
      onMoved();
    } else {
      toast.error(isFolder ? "Failed to move folder" : "Failed to move file");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(dialogOpen) => {
        if (!dialogOpen) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isFolder ? "Move folder" : "Move file"}</DialogTitle>
          <DialogDescription>
            Choose a destination for "{file?.name}".
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 text-sm flex-wrap">
          <VaultBreadcrumbs
            breadcrumbs={breadcrumbs}
            onNavigate={setDestinationFolderId}
          />
        </div>
        <div className="border rounded-md h-64 overflow-y-auto">
          {isLoadingEntries ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : visibleEntries.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Empty folder
            </div>
          ) : (
            <div className="p-1">
              {visibleFolders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent"
                  onClick={() => setDestinationFolderId(folder.id)}
                >
                  <Folder className="h-4 w-4 shrink-0 text-amber-600" />
                  <span className="truncate">{folder.name}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 ml-auto text-muted-foreground" />
                </button>
              ))}
              {visibleFiles.map((entry) => (
                <div
                  key={entry.id}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground"
                >
                  <File className="h-4 w-4 shrink-0" />
                  <span className="truncate">{entry.name}</span>
                </div>
              ))}
              {hiddenEntryCount > 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  +{hiddenEntryCount} more not shown
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isMoving}
          >
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={isMoving || isCurrentParent}>
            {isMoving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Move here"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
